import { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Seo from '../components/Seo';
import ProductCard from '../components/ProductCard';
import CategoryFilter from '../components/CategoryFilter';
import { api } from '../lib/api';
import type { CategorySlug } from '../config/brand';

export default function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCategory = (searchParams.get('category') as CategorySlug | null) ?? 'all';
  const [category, setCategory] = useState<CategorySlug | 'all'>(initialCategory);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (category === 'all') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', category);
    }
    setSearchParams(searchParams, { replace: true });
  }, [category, searchParams, setSearchParams]);

  const { data: products = [], isLoading } = useQuery({
    queryKey: ['products'],
    queryFn: api.listProducts,
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products
      .filter((p) => p.active)
      .filter((p) => category === 'all' || p.partitionKey === category)
      .filter((p) =>
        !q ||
        p.name.toLowerCase().includes(q) ||
        p.rowKey.toLowerCase().includes(q) ||
        (p.tagline ?? '').toLowerCase().includes(q),
      )
      .sort((a, b) => a.order - b.order);
  }, [products, category, search]);

  const availableCategories = useMemo(
    () => Array.from(new Set(products.filter((p) => p.active).map((p) => p.partitionKey))),
    [products],
  );

  return (
    <>
      <Seo title="Products" description="Browse Uclipse industrial cleaning concentrates." />

      <section className="relative overflow-hidden border-b border-slate-100 bg-gradient-to-br from-brand-50 via-white to-emerald-50/50">
        <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-brand-100/40 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 left-1/3 h-64 w-64 rounded-full bg-accent-green/10 blur-3xl" />
        <div className="container-page relative py-12 md:py-16">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/70 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-brand-700 ring-1 ring-brand-100">
            Product catalogue
          </span>
          <h1 className="mt-4 font-display text-4xl font-bold text-slate-900 md:text-5xl">Our Products</h1>
          <p className="mt-3 max-w-2xl text-lg text-slate-600">
            Concentrated, institutional-strength solutions for housekeeping, kitchen, laundry, automotive and
            kid-safe home care — engineered for performance, value and safety.
          </p>
        </div>
      </section>

      <section className="container-page py-10">
        <div className="sticky top-16 z-20 -mx-4 mb-8 border-b border-slate-100 bg-white/80 px-4 py-4 backdrop-blur md:static md:mx-0 md:border-0 md:bg-transparent md:px-0 md:py-0 md:backdrop-blur-0">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <CategoryFilter active={category} onChange={setCategory} available={availableCategories} />
            <div className="relative md:w-72">
              <svg
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.45 4.39l3.08 3.08a1 1 0 01-1.42 1.42l-3.08-3.08A7 7 0 012 9z"
                  clipRule="evenodd"
                />
              </svg>
              <input
                type="search"
                placeholder="Search products…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="input w-full pl-9"
              />
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="py-20 text-center text-slate-500">Loading products…</div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center text-slate-500">No products match your filters.</div>
        ) : (
          <>
            <p className="mb-5 text-sm text-slate-500">
              Showing <span className="font-semibold text-slate-700">{filtered.length}</span>{' '}
              {filtered.length === 1 ? 'product' : 'products'}
            </p>
            <div className="grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-4">
              {filtered.map((p) => (
                <ProductCard key={`${p.partitionKey}-${p.rowKey}`} product={p} />
              ))}
            </div>
          </>
        )}
      </section>
    </>
  );
}
